import axios, { AxiosInstance, AxiosResponse } from "axios";
import FetcherInput, { TypeRole } from "./FetcherInput";
import {
  FETCHER_CODE_EXPIRED_TOKEN,
  FETCHER_STATUS_TOKEN,
} from "./FetcherConsts";
import { IStorage } from "../Storage/IStorage";
import { v4 as uuidv4 } from "uuid";

export default class AxiosFetcher implements FetcherInput {
  private static ENABLE_REFRESH_TOKEN: boolean = true;
  private readonly _fetcher: AxiosInstance;
  private readonly _authFetcher: AxiosInstance;
  private readonly _authURL: string;
  private _refreshToken: string;
  private _token: string;
  private readonly _storage: IStorage;
  private readonly _config: any;
  private readonly _url: string;
  private _id: string;
  private _role: TypeRole | null;

  constructor(
    config: any,
    authURL: string,
    storage: IStorage,
    baseURL?: string
  ) {
    this._fetcher = axios.create({
      baseURL: baseURL ?? config.get("API_HOST"),
    });
    this._authFetcher = axios.create({
      baseURL: authURL,
    });
    this._url = baseURL ?? config.get("API_HOST");
    this._config = config;
    this._storage = storage;
    this._id = "";
    this._role = null;
    this._refreshToken = "";
    this._token = "";
    this._authURL = authURL;
    this.onRejected = this.onRejected.bind(this);
    this.getNewToken = this.getNewToken.bind(this);
    this.updateAuthorization = this.updateAuthorization.bind(this);
    this.onFulfilled = this.onFulfilled.bind(this);
    this._fetcher?.interceptors.response.use(this.onFulfilled, this.onRejected);
    this._fetcher?.interceptors.request.use((config) => {
      config.headers["Transaction-Id"] = uuidv4();
      return config;
    });
    // this._authFetcher.interceptors.response.use(
    //   this.onFulfilled,
    //   this.onRejected
    // );
  }

  public get request() {
    return this._fetcher;
  }

  public get authRequest() {
    return this._authFetcher;
  }

  public set refreshToken(refreshToken: string) {
    this._refreshToken = refreshToken;
  }

  public updateAuthorization(
    token: string,
    refreshToken: string,
    id: string,
    role: "CANDIDATE" | "STUDENT" | "STUDENTCLASS",
    idUserSupport: string | null
  ) {
    this._refreshToken = refreshToken;
    this._token = token;
    this._id = id;
    this._role = role;

    this._fetcher.defaults.headers.common = {
      Authorization: `Bearer ${token}`,
      "User-Id": id,
      "User-Role": role,
      "User-Id-To-Access": idUserSupport ?? "",
      "Transaction-Id": uuidv4(),
      // "User-Role-To-Access":
      //   roleUserSupport !== null ? roleUserSupport?.toLocaleUpperCase() : "",
    };
    this._authFetcher.defaults.headers.common = {
      Authorization: `Bearer ${token}`,
      "User-Id": id,
      "User-Role": role,
    };
  }

  public getId() {
    return this._id;
  }

  public async getNewToken(_refreshToken: string) {
    return new Promise<{ token: string; refreshToken: string }>(
      (resolve, reject) => {
        this._authFetcher
          .post("/refreshToken", {
            refreshToken: this._refreshToken,
          })
          .then((response) => {
            const { refreshToken, token } = response.data.data;
            const idUserSupport: string | null =
              window.localStorage.getItem("#idactive");
            this.updateAuthorization(
              token,
              refreshToken,
              this._id,
              this._role as TypeRole,
              idUserSupport
            );
            this._storage.setItem(
              this._config.get("STORAGE_AUTH"),
              JSON.stringify({ token, refreshToken })
            );
            resolve({ token, refreshToken });
          })
          .catch((error) => {
            if (axios.isCancel(error)) {
              // @ts-ignore
              resolve();
            } else {
              this._storage.clear();
              window.location.reload();
              reject(new Error(error));
            }
          });
      }
    );
  }

  private async onFulfilled(response: AxiosResponse) {
    return response;
  }

  private async onRejected(error: any) {
    const originalRequest = { ...error.response };
    const expiredToken =
      originalRequest &&
      !!originalRequest.data &&
      originalRequest.status === FETCHER_STATUS_TOKEN &&
      originalRequest.data.code === FETCHER_CODE_EXPIRED_TOKEN;
    if (Object.keys(originalRequest).length === 0 && expiredToken)
      window.location.reload();
    // const CancelToken = axios.CancelToken;
    // const source = CancelToken.source();
    if (
      expiredToken &&
      !error.config._retry &&
      AxiosFetcher.ENABLE_REFRESH_TOKEN
    ) {
      AxiosFetcher.ENABLE_REFRESH_TOKEN = false;
      error.config._retry = true;
      try {
        const { token } = await this.getNewToken(this._refreshToken);
        error.config.headers = {
          ...error.config.headers,
          Authorization: `Bearer ${token}`,
        };
        error.config._retry = false;
        AxiosFetcher.ENABLE_REFRESH_TOKEN = true;
        // this.source = axios.CancelToken.source();
        return this._fetcher(error.config);
      } catch (e) {
        error.config._retry = false;
        AxiosFetcher.ENABLE_REFRESH_TOKEN = true;
      }
    }
    return Promise.reject(error as Error);
  }
}
